import { Component, Injectable, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Color } from 'src/app/models/configurators-2d/applications/closet-doors-custom/color';
import { ColorCategory } from 'src/app/models/configurators-2d/applications/closet-doors-custom/color-category';
import { DoorStructure } from 'src/app/models/configurators-2d/applications/closet-doors-custom/door-structure';
import { Frame } from 'src/app/models/configurators-2d/applications/closet-doors-custom/frame';
import { FrameType } from 'src/app/models/configurators-2d/applications/closet-doors-custom/frame-type';
import { ClosetDoorsCustomService } from 'src/app/services/configurators-2d/applications/closet-doors-custom.service';
import { ClosetDoorsCustomConfiguratorItem } from '../../../configurator/applications/closet-doors-custom-configurator-item';
import { IBaseComponent } from '../../../configurator/i-base-component';
import { Configurators2DComponent } from '../../../configurators-2d.component';
import { ClosetDoorsCustomPreviewComponent } from '../preview/preview.component';
import { FrameFinish } from '../../../../../models/configurators-2d/applications/closet-doors-custom/frame-finish';
import { logError } from 'src/app/services/helpers/sentry.helper';

@Component({
  selector: 'ng-closet-doors-custom-finishes',
  templateUrl: './finishes.component.html',
})
@Injectable({
  providedIn: 'root',
})
export class ClosetDoorsCustomFinishesComponent
  extends ClosetDoorsCustomConfiguratorItem
  implements OnInit, IBaseComponent
{
  doorStructures: DoorStructure[];
  colorCategories: ColorCategory[];
  frames: Frame[];

  /** Sous-étape pour les sections à afficher ou non */
  subStep: number = 1;

  currentLang: string;
  baseImgURL: string;
  preview: ClosetDoorsCustomPreviewComponent;

  selectedColorCategory: ColorCategory;
  selectedDoor: number = 1;
  selectedDoorSection: number = 1;
  selectedFrameType: FrameType;

  /** Profilés compatibles */
  private compatibleFrames: Frame[] = [];

  /** Types de profilés à afficher */
  frameTypesToDisplay: FrameType[] = [];

  /** Profilés à afficher */
  framesToDisplay: Frame[] = [];

  /** Finitions de profilés à afficher */
  frameFinishesToDisplay: FrameFinish[] = [];

  /** Couleurs des catégories à afficher */
  colorsToDisplay: Color[] = [];

  constructor(
    private translate: TranslateService,
    closetDoorCustomService: ClosetDoorsCustomService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(closetDoorCustomService);
    this.currentLang = this.translate.getBrowserLang();
    this.baseImgURL = this.service.apiUrl;
    this.preview = configurators2DComponent.preview as ClosetDoorsCustomPreviewComponent;
  }

  ngOnInit(): void {
    this.toggleCollapse(1);

    this.preview.selectedDoor = 1;
    this.preview.selectedSection = 1;

    this.configuration.checkAccessories = true;

    this.preview.onDoorSelect.subscribe(event => {
      this.selectedDoor = event.selectedDoor;
      this.selectedDoorSection = event.selectedSection;
      this.updateColorCategories();
    });

    Promise.all([
      this.service
        .getDoorStructuresAndFinishes()
        .then(
          doorStructures => (this.doorStructures = [...doorStructures].sort((a, b) => a.displayOrder - b.displayOrder))
        ),
      this.service.getColors().then(colorCategories => (this.colorCategories = colorCategories)),
      this.service
        .getFrames()
        .then(frames => (this.frames = [...frames].sort((a, b) => a.displayOrder - b.displayOrder))),
    ]).then(() => {
      // Si la colorCategory ou color sélectionnée est déréférencée, les ajouter à la liste
      if (this.configuration.configurationDoorStructures) {
        for (const configurationDoorStructure of this.configuration.configurationDoorStructures) {
          for (const configurationDoorSection of configurationDoorStructure.configurationDoorSections) {
            let category = configurationDoorSection.doorSection.colorCategories.find(
              cc => cc.id === configurationDoorSection.color.colorCategoryId
            );

            if (category.isDisabled) {
              if (configurationDoorSection.color.isDisabled) {
                category.colors = [configurationDoorSection.color];
              }
              this.colorCategories.push(category);
            } else if (configurationDoorSection.color.isDisabled) {
              this.colorCategories
                .find(cc => cc.id === configurationDoorSection.color.colorCategoryId)
                .colors.push(configurationDoorSection.color);
            }
          }
        }
      }

      //si la frame ou frame finish est déréférencée, les ajouter à la liste
      if (this.configuration.frame) {
        if (this.configuration.frame.isDisabled) {
          if (this.configuration.frameFinish.isDisabled) {
            this.configuration.frame.frameFinishes = [this.configuration.frameFinish];
          }
          this.frames.push(this.configuration.frame);
        } else if (this.configuration.frameFinish.isDisabled) {
          this.frames
            .find(f => f.id === this.configuration.frame.id)
            .frameFinishes.push(this.configuration.frameFinish);
        }
      }

      this.computeFramesElements();

      if (
        !this.configuration.configurationDoorStructures ||
        this.configuration.configurationDoorStructures.length !== this.configuration.nbDoor
      ) {
        this.configuration.configurationDoorStructures = [];
        for (let i = 0; i < this.configuration.nbDoor; i++) {
          this.configuration.configurationDoorStructures.push({
            doorStructure: null,
            displayOrder: i + 1,
            configurationDoorSections: [],
          });
        }

        if (this.doorStructures && this.doorStructures.length > 0 && this.frames && this.frames.length > 0) {
          this.configuration.doorStructure = this.doorStructures[0];
          let frame = this.frames[0];
          this.selectFrameType(frame.frameType);

          if (frame.frameFinishes && frame.frameFinishes.length > 0) {
            frame.frameFinishes.sort((a, b) => a.displayOrder - b.displayOrder);
            this.selectFrameFinish(frame.frameFinishes[0]);
          }

          for (const configurationDoorStructure of this.configuration.configurationDoorStructures) {
            configurationDoorStructure.doorStructure = JSON.parse(JSON.stringify(this.configuration.doorStructure));
            configurationDoorStructure.doorStructureId = this.configuration.doorStructure.id;
            configurationDoorStructure.configurationDoorSections = [];

            for (const doorSection of configurationDoorStructure.doorStructure.doorSections) {
              const colorCategoriesIds: number[] = doorSection.colorCategorieIds.map(cc => cc.colorCategoryId);
              const colorCategories: ColorCategory[] = this.colorCategories.filter(c =>
                colorCategoriesIds.includes(c.id)
              );
              const color: Color = this.colorCategories.find(c => colorCategoriesIds.includes(c.id)).colors[0];

              doorSection.colorCategories = colorCategories;

              configurationDoorStructure.configurationDoorSections.push({
                doorSectionId: doorSection.id,
                doorSection: doorSection,
                colorId: color.id,
                color: color,
                colorComputedPath: this.computePathForKazedGraphics(
                  configurationDoorStructure.doorStructure.name,
                  color.picture
                ),
              });
            }
          }

          if (this.selectedDoor && this.selectedDoorSection) {
            let doorStructure = this.configuration.configurationDoorStructures?.find(
              d => d.displayOrder === this.selectedDoor
            );
            if (doorStructure) {
              let section = doorStructure.configurationDoorSections?.find(
                s => s.doorSection.index === this.selectedDoorSection
              );
              if (section?.doorSection?.colorCategorieIds && section?.color) {
                let colorCategory = section.doorSection.colorCategorieIds.find(cc =>
                  cc.colorIds.find(cId => cId === section.color.id)
                );
                if (colorCategory) {
                  this.selectColorCategory(this.colorCategories.find(cc => cc.id === colorCategory.colorCategoryId));
                }
              }
            }
          }
        }
        this.preview.getProfilesImages();
      } else {
        let doorStructure = this.configuration.configurationDoorStructures?.find(
          d => d.displayOrder === this.selectedDoor
        );

        if (doorStructure) {
          let section = doorStructure.configurationDoorSections?.find(
            s => s.doorSection?.index === this.selectedDoorSection
          );
          if (section?.doorSection?.colorCategories && section?.color) {
            let colorCategory = section.doorSection.colorCategories.find(cat =>
              cat.colors.find(col => col.id === section.color.id)
            );

            if (colorCategory) {
              this.selectColorCategory(colorCategory);
            }
          }
        }

        if (this.configuration.frame) {
          const frame = this.frames.find(f => f.id === this.configuration.frame.id);
          this.selectFrameType(frame.frameType);
        } else {
          this.preview.getProfilesImages();
        }
      }

      this.updateColorCategories();
      this.computeFramesElements();

      if (this.preview.isPreviewLoading == null) {
        this.preview.getProfilesImages();
        this.preview.update();
      }

      //Si la DoorStructure sélectionnée est déréférencée, l'ajouter à la liste
      if (
        this.configuration.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor)?.doorStructure
          ?.isDisabled
      ) {
        this.doorStructures.push(
          this.configuration.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor)?.doorStructure
        );
      }
    });
  }

  /**
   * Ouverture des sous-blocs
   * @param step
   */
  toggleCollapse(step: number): void {
    this.preview.finishesStep = step;
    this.subStep = step;
  }

  /**
   * Sélection des structures de portes
   * @param door
   */
  async selectDoorStructure(door: DoorStructure): Promise<void> {
    if (this.selectedDoor && this.isStructureCompatible(door)) {
      let configurationDoor = this.configuration.configurationDoorStructures.find(
        d => d.displayOrder === this.selectedDoor
      );
      configurationDoor.doorStructure = JSON.parse(JSON.stringify(door));
      configurationDoor.doorStructureId = door.id;
      configurationDoor.configurationDoorSections = [];

      // reset de la door section sélectionnée après le changement de doorstructure
      this.selectedDoorSection = 1;

      door.doorSections.forEach(section => {
        /** Check des catégories de couleurs compatibles avec la structure */
        const colorCategoriesIds: number[] = section.colorCategorieIds.map(cc => cc.colorCategoryId);
        const colorCategories: ColorCategory[] = this.colorCategories.filter(c => colorCategoriesIds.includes(c.id));
        const color: Color = this.colorCategories.find(c => colorCategoriesIds.includes(c.id)).colors[0];

        section.colorCategories = colorCategories;

        configurationDoor.configurationDoorSections.push({
          doorSectionId: section.id,
          doorSection: section,
          color: color,
          colorId: color.id,
          colorComputedPath: this.computePathForKazedGraphics(door.name, color.picture),
        });
      });

      if (this.selectedDoorSection) {
        let currentConfigurationDoorSection = configurationDoor.configurationDoorSections.find(
          s => s.doorSection.index === this.selectedDoorSection
        );

        if (currentConfigurationDoorSection) {
          this.selectColorCategory(
            currentConfigurationDoorSection.doorSection.colorCategories.find(cat =>
              cat.colors.find(col => col.id === currentConfigurationDoorSection.color.id)
            )
          );
        }
      }

      /** Check de la compatibilité des frames */
      this.computeFramesElements();

      if (this.compatibleFrames.length > 0) {
        const frame = this.compatibleFrames[0];
        this.selectFrameType(frame.frameType);
      }

      await this.preview.update();
      this.updateHasDisabledProduct();
    }
  }

  isStructureCompatible(door: DoorStructure): boolean {
    let isStructureCompatible: boolean = true;
    if (this.configuration.configurationDoorStructures) {
      let configDoorFramesIds: number[];
      this.configuration.configurationDoorStructures.forEach(d => {
        configDoorFramesIds = [];
        d.doorStructure.frameIds.forEach(id => {
          if (!configDoorFramesIds.includes(id)) configDoorFramesIds.push(id);
        });

        if (!door.frameIds.some(f => configDoorFramesIds.includes(f))) isStructureCompatible = false;
      });
    }

    if (door.isDisabled) {
      isStructureCompatible = false;
    }
    return isStructureCompatible;
  }

  /**
   * Détermination si la structure sélectionnée est désactivée ou non
   * @returns boolean
   */
  isDoorStructureValid(): boolean {
    return !this.configuration.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor)
      ?.doorStructure?.isDisabled;
  }

  /**
   * Détermination si la structure est sélectionnée ou non
   * @param id id de la structure
   * @returns boolean
   */
  isDoorStructureSelected(id: number): boolean {
    return (
      this.configuration.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor)?.doorStructure
        ?.id == id
    );
  }

  /**
   * Affichage des catégories de couleurs
   * @returns tableau de catégories de couleurs
   */
  getColorCategoryToDisplay(): ColorCategory[] {
    if (this.selectedDoorSection && this.selectedDoor) {
      let door = this.configuration.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor);
      let section = door.doorStructure.doorSections.find(e => e.index === this.selectedDoorSection);

      let colorCategoryIds = [];

      if (door && section) {
        /** Ajout des catégories qui sont liées par les sections de portes */
        if (door.doorStructure.maxColorCategories && door.doorStructure.maxSectionsForCategories) {
          // gestion des portes 50/50
          // si on a moins de catégorie sélectionnée que "maxSectionsForCategories" on propose toute les colorCategories disponibles
          const colorCategoryIdsSelected = [
            ...new Set(
              door.configurationDoorSections.filter(cds => cds.color != null).map(cds => cds.color.colorCategoryId)
            ),
          ];

          // permet de ne pas sélectionner plus de catégories de couleur qu'autorisé
          if (colorCategoryIdsSelected.length <= door.doorStructure.maxColorCategories) {
            section.colorCategorieIds.forEach(ccIds => {
              if (!colorCategoryIds.includes(ccIds.colorCategoryId)) {
                colorCategoryIds.push(ccIds.colorCategoryId);
              }
            });
          } else {
            colorCategoryIds.push(...colorCategoryIdsSelected);
          }
        } else if (
          section.doorSectionLinkColorCategories.length > 0 &&
          section.id === section.doorSectionLinkColorCategories[0].secondaryDoorSectionId
        ) {
          const id = door.configurationDoorSections
            .find(s => s.doorSectionId === section.doorSectionLinkColorCategories[0].primaryDoorSectionId)
            .doorSection.colorCategories.find(
              c =>
                c.colors.find(
                  color =>
                    color.id ===
                    door.configurationDoorSections.find(
                      s => s.doorSectionId === section.doorSectionLinkColorCategories[0].primaryDoorSectionId
                    ).color.id
                )?.id
            ).id;

          if (!colorCategoryIds.includes(id)) {
            colorCategoryIds.push(id);
          }
        } else {
          section.colorCategorieIds.forEach(e => {
            if (!colorCategoryIds.includes(e.colorCategoryId)) {
              colorCategoryIds.push(e.colorCategoryId);
            }
          });
        }

        /** Suppression des catégories qui doivent être uniques sur le vantail */
        if (door.doorStructure.uniqueColorCategoriesIds.length > 0) {
          door.configurationDoorSections.forEach(section => {
            let colorCategoryId = this.colorCategories.find(category =>
              category.colors.find(color => section.color.id === color.id)
            ).id;
            if (
              door.doorStructure.uniqueColorCategoriesIds.includes(colorCategoryId) &&
              section.doorSection.index !== this.selectedDoorSection
            ) {
              colorCategoryIds = colorCategoryIds.filter(c => !door.doorStructure.uniqueColorCategoriesIds.includes(c));
            }
          });
        }
      }

      return this.colorCategories.filter(c => colorCategoryIds.includes(c.id));
    } else {
      return [];
    }
  }

  /**
   * Sélection d'une catégorie de couleur
   * @param category catégorie de couleur
   */
  selectColorCategory(category: ColorCategory): void {
    this.selectedColorCategory = category;
    this.colorsToDisplay = this.getColorsToDisplay();
    this.updateHasDisabledProduct();
  }

  /**
   * Détermination si la catégorie est sélectionnée ou non
   * @param id id de la catégorie
   * @returns boolean
   */
  isColorCategorySelected(id: number): boolean {
    return this.selectedColorCategory?.id == id;
  }

  /**
   * Affichage des décors
   * @returns tableau de décors
   */
  getColorsToDisplay(): Color[] {
    const doorStructure = this.configuration.configurationDoorStructures.find(
      d => d.displayOrder === this.selectedDoor
    ).doorStructure;

    return this.selectedColorCategory?.colors
      .filter(x => doorStructure.canHaveHorizontalColor !== false || !x.isHorizontal)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Sélection d'un décor
   * @param color décor
   */
  selectColor(color: Color): void {
    if (this.selectedDoor && this.selectedDoorSection) {
      const door = this.configuration.configurationDoorStructures.find(d => d.displayOrder === this.selectedDoor);
      let section = door.configurationDoorSections.find(s => s.doorSection.index === this.selectedDoorSection);
      section.color = JSON.parse(JSON.stringify(color));
      section.colorId = section.color.id;
      section.colorComputedPath = this.computePathForKazedGraphics(door.doorStructure.name, section.color.picture);

      //Vantaux où les décors peuvent être A/B/B/A ou A/B/A/B
      if (door.doorStructure.maxColorCategories && door.doorStructure.maxSectionsForCategories) {
        // si on a plus de colorCategories sélectionnée que donnée dans la restriction
        // on update les sections qui avait la même color que celui qui vient d'être mis à jour
        const colorCategoryIdsSelected = door.configurationDoorSections
          .filter(cds => cds.color != null)
          .map(cds => cds.color.colorCategoryId);
        const counters = new Map();

        colorCategoryIdsSelected.forEach(id => {
          if (counters.has(id)) {
            counters.set(id, counters.get(id) + 1);
          } else {
            counters.set(id, 1);
          }
        });

        if (counters.size > door.doorStructure.maxColorCategories) {
          // Conditions si on a trop de catégories qaund on change une couleurs.
          const actualColorCategory = this.colorCategories.find(x => x.colors.some(x => x.id == color.id));
          counters.forEach((count, colorCategoryId) => {
            if (count < door.doorStructure.maxSectionsForCategories && colorCategoryId !== actualColorCategory.id) {
              let doorSections = door.configurationDoorSections.filter(
                cds =>
                  cds.color.colorCategoryId === colorCategoryId && cds.doorSection.index !== this.selectedDoorSection
              );
              doorSections.forEach(s => {
                s.color = color;
                s.colorId = color.id;
              });
            }
          });
        } else {
          counters.forEach((count, colorCategoryId) => {
            // si on a plus de sections sélectionnées que d'autorisées
            if (
              count > door.doorStructure.maxSectionsForCategories &&
              count % door.doorStructure.maxSectionsForCategories > 0
            ) {
              let doorSections = door.configurationDoorSections.filter(
                cds =>
                  cds.color.colorCategoryId === colorCategoryId &&
                  cds.doorSection.index > door.doorStructure.maxSectionsForCategories &&
                  cds.doorSection.index !== this.selectedDoorSection
              );

              // change la color des sections en trop
              const extraSection = count - door.doorStructure.maxSectionsForCategories;
              // si on a une doorSection, on peut changer la catégorie en l'autre dispo sur la porte
              if (doorSections.length > 0) {
                // color dispo
                const otherColor = door.configurationDoorSections.find(
                  cds => cds.color.colorCategoryId !== colorCategoryId
                ).color;

                for (let i = 0; i < extraSection; i++) {
                  const doorSection = doorSections[i];
                  doorSection.color = otherColor;
                  doorSection.colorId = section.color.id;
                }
              } else {
                // si on n'a pas de section disponible, on prend une section de l'autre couleur
                // pour la mettre avec la même couleur
                doorSections = door.configurationDoorSections.filter(
                  cds =>
                    cds.color.colorCategoryId !== colorCategoryId &&
                    cds.doorSection.index > door.doorStructure.maxSectionsForCategories &&
                    cds.doorSection.index !== this.selectedDoorSection
                );

                for (let i = 0; i < extraSection; i++) {
                  const doorSection = doorSections[i];
                  doorSection.color = section.color;
                  doorSection.colorId = section.color.id;
                }
              }
            }
          });
        }
      } else if (
        (section?.doorSection.doorSectionLinkColorCategories?.length > 0 &&
          section.doorSectionId === section?.doorSection.doorSectionLinkColorCategories[0].primaryDoorSectionId) ||
        (section?.doorSection?.doorSectionLinkColorCategories?.length > 0 &&
          section.doorSectionId === section?.doorSection?.doorSectionLinkColorCategories[0].primaryDoorSectionId)
      ) {
        //Vantaux où les sections sont liées par leur ID
        const doorSection = this.configuration.configurationDoorStructures
          .find(d => d.displayOrder === this.selectedDoor)
          .configurationDoorSections.find(
            s => s.doorSectionId === section.doorSection.doorSectionLinkColorCategories[0].secondaryDoorSectionId
          );

        doorSection.color = color;
      } else if (this.configuration.doorRange.hasSameDoor) {
        this.configuration.configurationDoorStructures.forEach(structure => {
          if (this.configuration.doorRange.sameDoorFrameIds.length > 0) {
            if (this.configuration.doorRange.sameDoorFrameIds.includes(this.configuration.frame.id)) {
              structure.configurationDoorSections.find(s => s.doorSection.index === this.selectedDoorSection).color =
                color;
            }
          } else {
            structure.configurationDoorSections.find(s => s.doorSection.index === this.selectedDoorSection).color =
              color;
          }
        });
      }

      this.computeFramesElements();

      if (this.compatibleFrames) {
        this.selectFrameType(this.compatibleFrames[0].frameType);
      }

      this.updateHasDisabledProduct();
    }
  }

  private computePathForKazedGraphics(doorName: string, picture: any): string {
    if (picture) {
      if (
        this.configuration.supplier.name === 'Kazed' &&
        picture.name.startsWith('Color_Graphics_') &&
        doorName &&
        !doorName.includes('Origine')
      ) {
        const colorName = picture.name.replace('Color_Graphics_', '').toLowerCase();
        const index = picture.path.indexOf(colorName);
        let path = picture.path.substring(0, index);
        const suffix = doorName.replace(' ', '_').toLowerCase().replace('1', 'gauche').replace('2', 'droite');
        return path + colorName + '_' + suffix + '.jpg';
      }
    }

    return undefined;
  }

  /**
   * Calcule toutes les frames compatibles avec la configuration
   */
  private findCompatibleFrames(): void {
    if (this.configuration.configurationDoorStructures?.find(_ => _)?.doorStructure) {
      let categoryColorIds = [];
      let colorIds = [];
      let frames: Frame[] = [];
      let frameIdsToDelete = [];

      this.configuration.configurationDoorStructures.forEach(door => {
        door.configurationDoorSections.forEach(section => {
          let categoryId = this.colorCategories.find(category =>
            category.colors.find(color => section.color.id === color.id)
          ).id;
          let colorId = section.color.id;

          // récupération des colorCategories utilisées
          if (!categoryColorIds.includes(categoryId)) {
            categoryColorIds.push(categoryId);
          }

          // récupération des colors utilisées
          if (!colorIds.includes(colorId)) {
            colorIds.push(colorId);
          }
        });
      });

      if (this.configuration.frame) {
        this.frames.forEach(frame => {
          let frameRestricted: Frame;

          // si la frame n'a pas de restriction sur la colorCategory
          if (frame.colorCategoryIds.length === 0) {
            frameRestricted = frame;
          }

          // si la frame a des restrictions sur la colorCategory et que l'une des portes n'utilise pas cette catégorie
          let restrictColorCategory = false;
          for (const configurationDoorStructure of this.configuration.configurationDoorStructures) {
            for (const configurationDoorSection of configurationDoorStructure.configurationDoorSections) {
              if (
                !frame.colorCategoryIds.some(c => c.colorCategoryId === configurationDoorSection.color.colorCategoryId)
              ) {
                restrictColorCategory = true;
                break;
              }
            }

            if (restrictColorCategory) {
              break;
            }
          }

          if (!restrictColorCategory) {
            frameRestricted = frame;
          }

          // si la frame n'est pas contrainte à une certaine couleur
          if (frameRestricted && !frame.restrictedColorIds.some(id => colorIds.includes(id))) {
            // dans ce cas, on l'ajoute au frame possible d'utiliser
            frames.push(frameRestricted);
          }
        });
      } else {
        Object.assign(frames, this.frames);
      }

      for (const frame of frames) {
        for (const configurationDoorStructure of this.configuration.configurationDoorStructures) {
          if (!configurationDoorStructure.doorStructure.frameIds.includes(frame.id)) {
            frameIdsToDelete.push(frame.id);
          }
        }
      }

      this.compatibleFrames = frames.filter(f => !frameIdsToDelete.includes(f.id));
    } else {
      this.compatibleFrames = this.frames;
    }
  }

  /**
   * Vérifie si les décors sont utilisables dans la config
   */
  isDecorValid(): boolean {
    if (
      !this.configuration.configurationDoorStructures ||
      this.configuration.configurationDoorStructures.length === 0 ||
      !this.selectedDoor ||
      !this.selectedDoorSection
    ) {
      return true;
    }

    let door = this.configuration.configurationDoorStructures.find(d => d.displayOrder === this.selectedDoor);

    // vérifie qu'une porte correspond à l'index
    if (!door) {
      return true;
    }

    let doorSection = door.configurationDoorSections.find(s => s.doorSection.index === this.selectedDoorSection);

    // vérifie qu'une section correspond à l'index
    if (!doorSection) {
      return true;
    }

    if (doorSection.color) {
      return (
        this.colorCategories &&
        !doorSection.color.isDisabled &&
        !this.colorCategories.find(c => c.id === doorSection.color.colorCategoryId)?.isDisabled
      );
    }

    return true;
  }

  /**
   * Détermination si le décor est sélectionné ou non
   * @param id id du décor
   * @returns boolean
   */
  isColorSelected(id: number): boolean {
    if (this.selectedDoor && this.selectedDoorSection) {
      let door = this.configuration.configurationDoorStructures.find(d => d.displayOrder === this.selectedDoor);
      let doorSection = door.configurationDoorSections.find(s => s.doorSection.index === this.selectedDoorSection);

      if (doorSection.color) {
        return doorSection.color.id == id;
      }
    }

    return false;
  }

  /**
   * Mise à jour des catégories de couleurs en fonction du vantail sélectionné
   */
  updateColorCategories(): void {
    if (this.selectedDoor && this.selectedDoorSection) {
      let door = this.configuration?.configurationDoorStructures?.find(d => d.displayOrder === this.selectedDoor);
      let doorSection = door?.configurationDoorSections?.find(s => s.doorSection.index === this.selectedDoorSection);
      this.selectColorCategory(this.colorCategories.find(cc => cc.id === doorSection.color.colorCategoryId));
    }
  }

  /**
   * Sélection d'un profilé et de la finition
   * @param frame
   */
  selectFrame(frame: Frame): void {
    /* Si pas de profilé en paramètre*/
    if (
      !frame ||
      /*Ou si le profilé en paramètre est identique à celui de la config courante ET si finition il y a elle est disponible dans les finitions disponibles alors pas de changement.*/
      (frame.id === this.configuration.frame?.id &&
        this.configuration?.frameFinish &&
        this.frameFinishesToDisplay.includes(this.configuration.frameFinish))
    ) {
      return;
    }

    this.configuration.frame = this.frames.find(f => f.id == frame.id) || this.frames.find(_ => _);

    this.getFramesFinishesToDisplay();

    if (this.configuration.frameFinish) {
      let frameFinish = this.frameFinishesToDisplay.find(f => f.name == this.configuration.frameFinish.name);

      if (!frameFinish) {
        frameFinish = this.frameFinishesToDisplay[0];
      }

      this.selectFrameFinish(frameFinish);
    } else {
      this.selectFrameFinish(this.frameFinishesToDisplay[0]);
    }

    // Si porte identique en fonction du profilé
    if (
      this.configuration.doorRange.hasSameDoor &&
      this.configuration.doorRange.sameDoorFrameIds.length > 0 &&
      this.configuration.doorRange.sameDoorFrameIds.includes(this.configuration.frame?.id)
    ) {
      if (this.configuration.configurationDoorStructures[0]?.configurationDoorSections[0]?.color !== undefined) {
        this.selectColor(this.configuration.configurationDoorStructures[0]?.configurationDoorSections[0]?.color);
      }
    }
    this.preview.getProfilesImages();
    this.updateHasDisabledProduct();
  }

  /**
   * Détermination si le profilé est sélectionné ou non
   * @param id id du profilé
   * @returns boolean
   */
  isFrameSelected(id: number): boolean {
    return this.configuration.frame?.id == id;
  }

  /**
   * Permet de lancer les calculs pour savoir :
   * - Quels Frame sont autorisés dans la configuration
   * - Quels FrameTypes/Frames/FrameFinishes sont affichables
   * @private
   */
  private computeFramesElements(): void {
    this.findCompatibleFrames();
    this.getFramesToDisplay();
    this.getFrameTypesToDisplay();
    this.getFramesFinishesToDisplay();
  }

  /**
   * Affichage des catégories de profilés
   * @returns tableau de catégories de profilés
   */
  private getFrameTypesToDisplay(): void {
    let frameTypes: FrameType[] = [];

    if (this.compatibleFrames.length > 0) {
      for (const compatibleFrame of this.compatibleFrames) {
        if (!frameTypes.find(c => c.id === compatibleFrame.frameType.id)) {
          frameTypes.push({
            id: compatibleFrame.frameType.id,
            name: compatibleFrame.frameType.name,
            icon: compatibleFrame.icon.path,
          });
        }
      }
    } else if (this.frames) {
      for (const frame of this.frames) {
        if (!frameTypes.find(c => c.id === frame.frameType.id)) {
          frameTypes.push({
            id: frame.frameType.id,
            name: frame.frameType.name,
            icon: frame.icon.path,
          });
        }
      }
    }

    this.frameTypesToDisplay = frameTypes;
  }

  /**
   * Sélection d'une catégorie de profilé
   * @param frameType catégorie de profilé
   */
  selectFrameType(frameType: FrameType): void {
    this.selectedFrameType = frameType;

    // Sélection de la première frame du frameType
    const frames = this.compatibleFrames.filter(f => f.frameType.id === this.selectedFrameType.id);
    const frame =
      this.configuration.frame && frames.find(f => f.id === this.configuration.frame.id)
        ? frames.find(f => f.id === this.configuration.frame.id)
        : frames[0];

    this.getFramesToDisplay();
    this.selectFrame(frame);
  }

  /**
   * Détermination si la catégorie de profilé est sélectionnée ou non
   * @param id id de la catégorie de profilé
   * @returns boolean
   */
  isFrameTypeSelected(id: number): boolean {
    return this.selectedFrameType.id === id;
  }

  /**
   * Sélection d'une finition de profilé
   * @param frameFinish
   */
  selectFrameFinish(frameFinish: FrameFinish): void {
    if (!this.configuration.frameFinish || frameFinish.id !== this.configuration.frameFinish.id) {
      // reset des accessoires (pas forcément les mêmes contraintes d'un frame à l'autre)
      this.configuration.configurationAccessories = [];

      this.configuration.frameFinish = frameFinish;
      this.preview.getProfilesImages();
      this.updateHasDisabledProduct();
    }
  }

  /**
   * Récupération des finitions de profilés compatibles
   * @private
   */
  private getFramesFinishesToDisplay(): void {
    // si au moins une porte contient une restriction sur les finitions
    if (
      this.configuration.configurationDoorStructures?.find(
        door => door.doorStructure?.restrictedFrameFinishIds.length > 0
      )
    ) {
      let restricedframeFinishIds = [];

      // récupération des finitions possibles d'utiliser
      this.configuration.configurationDoorStructures.forEach(door => {
        door.doorStructure.restrictedFrameFinishIds.forEach(id => {
          if (!restricedframeFinishIds.includes(id)) {
            restricedframeFinishIds.push(id);
          }
        });
      });

      this.frameFinishesToDisplay = this.configuration.frame.frameFinishes.filter(
        f => !restricedframeFinishIds.includes(f.id)
      );
    } else if (this.configuration.frame && this.frames) {
      let frame = this.frames.find(f => f.id == this.configuration.frame.id);
      if (!frame) {
        frame = this.frames.find(() => true);
      }
      this.frameFinishesToDisplay = frame.frameFinishes;
    } else {
      this.frameFinishesToDisplay = [];
    }
  }

  isFrameFinishValid(): boolean {
    let valid: boolean = true;
    if (this.configuration.frame) {
      valid = !this.configuration.frame.isDisabled;
    }

    if (valid && this.configuration.frameFinish) {
      valid = !this.configuration.frameFinish.isDisabled;
    }

    return valid;
  }

  /**
   * Affichage des profilés
   * @returns tableau de profilés
   */
  private getFramesToDisplay(): void {
    if (!this.selectedFrameType) {
      return;
    }

    if (this.compatibleFrames.length > 0) {
      this.framesToDisplay = this.compatibleFrames.filter(f => f.frameType.id === this.selectedFrameType.id);
    } else {
      logError('getFramesToDisplay() : aucun profilé compatible trouvé');
      this.framesToDisplay = this.frames.filter(f => f.frameType.id === this.selectedFrameType.id);
    }
  }

  /**
   * Détermination si la finition de profil est sélectionnée ou non
   * @param id id de la finition de profil
   * @returns boolean
   */
  isFrameFinishSelected(id: number): boolean {
    return this.configuration.frameFinish?.id == id;
  }

  /**
   * Détermination si le bouton suivant est actif
   * @returns boolean
   */
  isNextButtonActive(): boolean {
    return (
      this.configuration.frame != null &&
      this.configuration.configurationDoorStructures != null &&
      !this.configuration.frame.isDisabled &&
      !this.configuration.frameFinish.isDisabled &&
      this.configuration.configurationDoorStructures?.every(
        ds =>
          !ds.doorStructure.isDisabled &&
          ds.configurationDoorSections.every(
            s =>
              !s.color.isDisabled &&
              !s.doorSection.colorCategories.find(cc => cc.id === s.color.colorCategoryId).isDisabled
          )
      )
    );
  }

  /**
   * Détermination si on peut accéder à la page
   * @returns boolean
   */
  checkPageChangeCapability(): boolean {
    return (
      this.configuration.supplier != null &&
      this.configuration.doorRange != null &&
      !this.configuration.doorRange.isDisabled
    );
  }

  /**
   * Détermine si on doit afficher les finitions de profilé
   * @returns true si la section doit s'afficher, false sinon
   */
  displayFrameFinishes(): boolean {
    return (
      this.frameFinishesToDisplay.length > 0 && this.framesToDisplay.some(f => f.id === this.configuration.frame?.id)
    );
  }

  /**
   * Calcule si la configuration a des produits désactivés
   */
  updateHasDisabledProduct(): void {
    if (this.configuration.hasDisabledProducts) {
      let hasDisabledProduct: boolean = false;

      if (this.configuration.frame && this.configuration.configurationDoorStructures && this.configuration.doorRange) {
        if (
          this.configuration.doorRange.isDisabled ||
          this.configuration.frame.isDisabled ||
          this.configuration.frameFinish.isDisabled ||
          this.configuration.configurationDoorStructures.find(cd => cd.doorStructure.isDisabled) != null ||
          this.configuration.configurationDoorStructures.find(
            cd =>
              cd.configurationDoorSections.find(ds => ds.color.isDisabled) ||
              this.configuration.configurationDoorStructures.find(cd =>
                cd.configurationDoorSections.find(ds =>
                  ds.doorSection.colorCategories.find(cc => cc.id === ds.color.colorCategoryId && cc.isDisabled)
                )
              )
          )
        ) {
          hasDisabledProduct = true;
        }
      }

      if (this.configuration.configurationAccessories.find(ca => ca.accessory.isDisabled)) {
        hasDisabledProduct = true;
      }

      this.configuration.hasDisabledProducts = hasDisabledProduct;
    }
  }
}
